import React, { useId } from "react";
import Box from "@mui/material/Box";

type BrandLoaderProps = {
  size?: number;
  /** Full-viewport centered loader (app boot / page gate). */
  fullscreen?: boolean;
  label?: string;
};

export const BrandLoader: React.FunctionComponent<BrandLoaderProps> = ({
  size = 56,
  fullscreen = false,
  label = "Loading",
}) => {
  const uid = useId().replace(/:/g, "");
  const markerId = `brandLoaderAh-${uid}`;

  const mark = (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 512"
      fill="none"
      aria-hidden
      sx={{ width: size, height: size, display: "block", overflow: "visible" }}
    >
      <defs>
        <marker
          id={markerId}
          viewBox="0 0 10 10"
          refX="3"
          refY="5"
          markerWidth="2.4"
          markerHeight="2.4"
          orient="auto"
        >
          <path d="M0 0 L10 5 L0 10 z" fill="#2563EB" />
        </marker>
      </defs>
      <g>
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 256 256"
          to="360 256 256"
          dur="1.05s"
          repeatCount="indefinite"
        />
        <path
          d="M 73.8 223.9 A 185 185 0 0 1 397.7 137.1"
          fill="none"
          stroke="#2563EB"
          strokeWidth="34"
          strokeLinecap="round"
          markerEnd={`url(#${markerId})`}
        />
        <path
          d="M 438.2 288.1 A 185 185 0 0 1 114.3 374.9"
          fill="none"
          stroke="#2563EB"
          strokeWidth="34"
          strokeLinecap="round"
          markerEnd={`url(#${markerId})`}
        />
      </g>
      <text
        x="256"
        y="340"
        textAnchor="middle"
        fontFamily="Arial, Helvetica, sans-serif"
        fontWeight="bold"
        fill="#2563EB"
      >
        <tspan fontSize="235">R</tspan>
        <tspan fontSize="165">x</tspan>
      </text>
    </Box>
  );

  if (fullscreen) {
    return (
      <Box
        role="status"
        aria-label={label}
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {mark}
      </Box>
    );
  }

  return (
    <Box
      role="status"
      aria-label={label}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 0,
      }}
    >
      {mark}
    </Box>
  );
};

/** MUI DataGrid `slots.loadingOverlay` */
export const BrandLoaderOverlay: React.FunctionComponent = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      width: "100%",
    }}
  >
    <BrandLoader size={48} />
  </Box>
);

export default BrandLoader;
